import React from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PurchasePricing from 'components/purchase/PurchasePricing';
import DiscountCode from 'components/purchase/DiscountCode';
import PoweredBy from 'components/PoweredBy';
import Timer from 'components/purchase/Timer';
import TripSummaryCard from 'components/purchase/TripSummaryCard';
import {
  shouldShowTripRouteSummary,
  TripRouteSummaryCardContainer,
} from 'components/purchase/TripRouteSummaryCard';
import { useTranslation } from 'react-i18next';
import { Spacing, Text, Link, Visibility, Icon, Gap } from '@reservamos/elements';
import WelcomeDigitalDiscountModal from 'components/search/ModalContent/WelcomeDigitalDiscountModal';
import shouldShowWelcomeDigitalDiscount from 'utils/welcomeDigitalDiscountValidation';
import whatsappIcon from 'images/icons/whatsapp-icon.svg';
import getPurchaseStepName from '../../utils/purchase/getPurchaseStepName';
import BackButton from '../../ui/atoms/BackButton';
import LoyaltyButton from '../LoyaltyButton';
import useLoyaltyPrograms from '../../loyalty/context/useLoyaltyPrograms';
import DotersBanner from '../../ui/atoms/DotersBanner';
import DotersHeader from '../../ui/molecules/DotersHeader';
import DotersBannerAccount from '../../ui/atoms/DotersBannerAccount';

const propTypes = {
  showDiscountCode: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  showPurchaseReview: PropTypes.func.isRequired,
  goToSearch: PropTypes.func.isRequired,
  isOpenTicket: PropTypes.bool.isRequired,
  isOnDemand: PropTypes.bool.isRequired,
  isExchange: PropTypes.bool,
  timerRemainingTime: PropTypes.number,
  roundTrip: PropTypes.bool.isRequired,
  departureIsOpenTicket: PropTypes.bool,
  returnIsOpenTicket: PropTypes.bool,
  originCityName: PropTypes.string,
  destinationCityName: PropTypes.string,
};

/**
 * PurchaseSidebar component.
 * Renders the sidebar for purchase pages with pricing, discount codes, and additional features.
 * @param {Object} props - Component props
 * @param {boolean} props.isLoading - Whether the component is in loading state
 * @param {Function} props.showPurchaseReview - Function to show purchase review
 * @param {Function} props.goToSearch - Function to navigate to search
 * @param {boolean} props.isOpenTicket - Whether the ticket is open
 * @param {boolean} props.isOnDemand - Whether the service is on demand
 * @param {boolean} props.isExchange - Whether this is an exchange transaction
 * @param {number} props.timerRemainingTime - Remaining time for the timer
 * @param {boolean} props.roundTrip - Whether this is a round trip
 * @param {boolean} props.departureIsOpenTicket - Whether departure is open ticket
 * @param {boolean} props.returnIsOpenTicket - Whether return is open ticket
 * @param {string} props.originCityName - Origin city name
 * @param {string} props.destinationCityName - Destination city name
 * @returns {JSX.Element} - PurchaseSidebar component
 */
const PurchaseSidebar = ({
  isLoading,
  showDiscountCode,
  showPurchaseReview,
  goToSearch,
  isOpenTicket,
  isOnDemand,
  isExchange,
  timerRemainingTime,
  roundTrip,
  departureIsOpenTicket,
  returnIsOpenTicket,
  originCityName,
  destinationCityName,
}) => {
  const location = useLocation();
  const {
    features,
    copies,
    env: { brand },
  } = useSelector((state) => state.whitelabelConfig);

  // Transporter abbr of the departure trip, used to resolve a per-transporter
  // customer service email when the tenant groups several transporters.
  const purchase = useSelector((state) => state.purchase);
  const departureTransporterAbbr = purchase?.getIn?.(['departs', 'fragments', 0, 'line', 'abbr']);
  const typeOfSearch = purchase?.getIn?.(['tenant', 'typeOfSearch']);
  const showTripRouteSummary = shouldShowTripRouteSummary(
    typeOfSearch,
    features.TRIP_ROUTE_SUMMARY_DISABLED,
  );

  const {
    costaPass: { userIsLoggedIn: userIsLoggedInWithCostaPass, config: costaPassConfig },
    doters: { userIsLoggedIn: userIsLoggedInWithDoters },
  } = useLoyaltyPrograms();
  const pricingBeforeCheckoutIsOn = features.PRICING_BEFORE_CHECKOUT;
  const { t } = useTranslation('purchase');

  const SHOW_ON_FLAT = features.FUNNEL_STYLE === 'FLAT';
  const { SHOW_TRIP_DETAIL_CARD } = features;
  const showDetailCard = SHOW_ON_FLAT || SHOW_TRIP_DETAIL_CARD;
  const showSiemprePlus = !isOpenTicket && !isOnDemand;
  const showCostaPassBanner =
    !isOpenTicket && features.COSTAPASS_ENABLED && !userIsLoggedInWithCostaPass;

  const purchaseStep = getPurchaseStepName(location.pathname);
  const showPricing =
    ['checkout', 'complete', 'quickCheckout'].includes(purchaseStep) || pricingBeforeCheckoutIsOn;
  // `showDiscountCode` is false only while Quick Purchase reports a state where a coupon must not
  // be offered (issued store reference, blocking payment attempt, inline seat editing). It defaults
  // to true, so the standard checkout behaves exactly as before.
  const showDiscountCouponForm =
    ['checkout', 'quickCheckout'].includes(purchaseStep) && showDiscountCode;
  const isQuickCheckoutScreen = purchaseStep === 'quickCheckout';
  const isCheckout = purchaseStep === 'checkout';
  const signMessagesInvitation =
    !userIsLoggedInWithDoters && features.DOTERS_ENABLED
      ? !isCheckout && <DotersBanner show onSidebar />
      : null;

  const showWelcomeDigitalDiscountBanner = shouldShowWelcomeDigitalDiscount({
    originCityName,
    destinationCityName,
  });

  const providerPhones = copies.provider.phones || [];
  // Prefer the per-transporter email when configured; fall back to the
  // tenant-wide provider email so other tenants are unaffected.
  const providerEmail =
    copies.provider.emailByTransporter?.[departureTransporterAbbr] || copies.provider.email;
  const whatsappMessage = encodeURIComponent(t('text.whatsapp_default_message'));
  const phoneItems = providerPhones.map((phone) => {
    const isWhatsapp = phone.type?.toLowerCase() === 'whatsapp';
    const href = isWhatsapp
      ? `https://wa.me/${phone.value}?text=${whatsappMessage}`
      : `tel:${phone.value}`;

    return (
      <div
        key={`${isWhatsapp ? 'whatsapp' : 'tel'}-${phone.value}`}
        className="purchase-sidebar-contact-item"
      >
        <Gap display="inline-flex" alignItems="center" columnGap="XS">
          {isWhatsapp && <Icon size="S" type={whatsappIcon} />}
          <Link text={phone.display} href={href} type="accent" newTab={isWhatsapp} />
        </Gap>
        {phone.label && (
          <Text elementType="span" size="S" color="grayLight">{` ${phone.label}`}</Text>
        )}
      </div>
    );
  });

  if (isQuickCheckoutScreen) {
    return (
      <div className="purchase-sidebar">
        <div className="bg-gray-100 rounded-xl p-5 lg:-mt-10">
          <Spacing vertical size="L">
            <Timer remainingTime={timerRemainingTime} />
            {showTripRouteSummary && (
              <TripRouteSummaryCardContainer
                onChangeSchedule={goToSearch}
                onShowDetails={showPurchaseReview}
              />
            )}
            <div>
              <div className="purchase-sidebar-pricing flex flex-col p-3 gap-y-4 bg-white rounded-xl">
                <div
                  className={
                    isLoading
                      ? 'purchase-sidebar-loading'
                      : 'flex flex-col gap-y-4 bg-white rounded-xl w-full'
                  }
                >
                  <PurchasePricing />
                  {!isExchange && showDiscountCouponForm && <DiscountCode />}
                </div>
              </div>
            </div>

            <div className="purchase-sidebar-contact">
              {!SHOW_ON_FLAT && (
                <Text weight="semibold" size="S">
                  {t('label.we_can_help', { context: brand })}
                </Text>
              )}

              <Spacing vertical size="S">
                <Text weight="semibold" size="S">
                  {t('general:customer_service')}
                </Text>
                <Spacing vertical size="XS">
                  {phoneItems}
                  {providerEmail && (
                    <Link text={providerEmail} href={`mailto:${providerEmail}`} type="accent" />
                  )}
                  {copies.provider.nit && (
                    <Text size="S" color="accent" weight="semibold">
                      NIT: {copies.provider.nit}
                    </Text>
                  )}
                  {copies.provider.frequentQuestions && (
                    <Link
                      text={copies.provider.frequentQuestions.label}
                      href={copies.provider.frequentQuestions.link}
                      type="accent"
                      newTab
                    />
                  )}
                </Spacing>
              </Spacing>
            </div>
            <PoweredBy />
          </Spacing>
        </div>
      </div>
    );
  }

  return (
    <div className="purchase-sidebar">
      <Spacing vertical size="L">
        {!showTripRouteSummary && (
          <BackButton
            onClick={goToSearch}
            isRoundTripWithOpenDeparture={roundTrip && departureIsOpenTicket && !returnIsOpenTicket}
            isRoundTripWithBothOpen={roundTrip && departureIsOpenTicket && returnIsOpenTicket}
          />
        )}
        <Timer remainingTime={timerRemainingTime} />
        <DotersBannerAccount />
        {showWelcomeDigitalDiscountBanner && (
          <Visibility type="hideForMobileOnly">
            <WelcomeDigitalDiscountModal
              screen={purchaseStep === 'passengers' ? 'passengers' : 'checkout'}
            />
          </Visibility>
        )}
        <DotersHeader />
        {showPricing && (
          <div>
            {SHOW_ON_FLAT && (
              <Text weight="semibold" size="S">
                {t('label.payment_details')}
              </Text>
            )}

            <div className="purchase-sidebar-pricing">
              <div
                className={`purchase-sidebar${
                  isLoading ? '-loading' : ''
                } flex flex-col p-3 gap-y-4`}
              >
                <PurchasePricing />
                {!isExchange && showDiscountCouponForm && <DiscountCode />}
              </div>
            </div>
          </div>
        )}

        {showTripRouteSummary ? (
          <TripRouteSummaryCardContainer
            onChangeSchedule={goToSearch}
            onShowDetails={showPurchaseReview}
          />
        ) : (
          showDetailCard && (
            <div>
              <Text weight="semibold" size="S">
                {t('label.trip_detail')}
              </Text>
              <TripSummaryCard showPurchaseReview={showPurchaseReview} />
            </div>
          )
        )}

        {showSiemprePlus && signMessagesInvitation}

        {showCostaPassBanner && (
          <LoyaltyButton
            extraConfig={costaPassConfig}
            id="siempre-plus-widget-sidebar-button-costapass"
            insideIcon
          />
        )}

        <div className="purchase-sidebar-contact">
          {!SHOW_ON_FLAT && (
            <Text weight="semibold" size="S">
              {t('label.we_can_help', { context: brand })}
            </Text>
          )}

          <Spacing vertical size="S">
            <Text weight="semibold" size="S">
              {t('general:customer_service')}
            </Text>
            <Spacing vertical size="XS">
              {phoneItems}
              {providerEmail && (
                <Link text={providerEmail} href={`mailto:${providerEmail}`} type="accent" />
              )}
              {copies.provider.nit && (
                <Text size="S" color="accent" weight="semibold">
                  NIT: {copies.provider.nit}
                </Text>
              )}
              {copies.provider.frequentQuestions && (
                <Link
                  text={copies.provider.frequentQuestions.label}
                  href={copies.provider.frequentQuestions.link}
                  type="accent"
                  newTab
                />
              )}
            </Spacing>
          </Spacing>
        </div>
        {features.SHOW_POWERED_BY && <PoweredBy />}
      </Spacing>
    </div>
  );
};

PurchaseSidebar.defaultProps = { showDiscountCode: true };

PurchaseSidebar.propTypes = propTypes;

export default PurchaseSidebar;
